/* eslint-disable arrow-body-style */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Theme } from "@emotion/react";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { Status } from "@iventis/domain-model/model/status";
import { findFirstParentByType, findNode, findNodes, getNodesWithoutParent } from "@iventis/tree-browser/tree-operations";
import { CompositionMapObject, machineEventTypes, MappingEngine } from "@iventis/map-engine";
import React, { BaseSyntheticEvent, FunctionComponent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@iventis/styles";
import { useTreeBrowser } from "@iventis/tree-browser";
import { TreeBrowserDomainNodeContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { v4 as uuid } from "uuid";
import { ExportMapDialog } from "@iventis/map-export";
import { ExportOptions } from "@iventis/map-engine/src/bridge/mapbox/engine-mapbox-types-and-constants";
import { ExportDwgDialog, KeyboardShortcutToolTipComponent, LoadingComponent, NodeWithMapNode, StyledCustomDialog, UnlinkOrCopyLayerDialog } from "@iventis/components";
import { useUpgradePlanAlert } from "@iventis/permissions/src/upgrade-subscription-alert";
import {
    canRead,
    parseEpochSecondsToDateOnlyString,
    parseTimeHHMMNumbertoHHMMSSString,
    splitArrayByLength,
    useCtrlKey,
    useFunctionality,
    useReadWritePermissionCheck,
} from "@iventis/utilities";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { libraryApi } from "@iventis/api/src/api";
import { useDataFieldServices } from "@iventis/datafield-editor";
import { DialogTitle, useTheme } from "@mui/material";
import { ObjectUpdate } from "@iventis/map-engine/src/types/events-store";
import { ImportGeoJsonContext, ImportGeojsonFileUploadComponent, PostImportedGeoJsonMapObject } from "@iventis/map-export-import-mapper";
import { StyledBar } from "@iventis/components/src/sidebar";
import { BottomUpHierarchy } from "@iventis/domain-model/model/bottomUpHierarchy";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { UserScope } from "@iventis/domain-model/model/userScope";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { getSelectedSitemapVersions } from "@iventis/map-engine/src/utilities/sitemap-helpers";
import { getMapObjectNameSystemDatafield } from "@iventis/datafields";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { MapObjectExportCadOptions } from "@iventis/domain-model/model/mapObjectExportCadOptions";
import { PAGE_SIZES_FOR_RASTER } from "@iventis/map-export/src/export-helpers";
import { useMapScreenshot } from "./hooks/use-map-screenshot";
import { useGroupLayerTooltip } from "./hooks/use-group-layer-tooltip";
import { useUnlinkLayerTooltip } from "./hooks/use-unlink-layer-tooltip";
import { useCopyLayerTooltip } from "./hooks/use-copy-layer-tooltip";
import { useDeleteActionTooltip } from "./hooks/use-delete-action-tooltip";
import { RootState, RootDispatch } from "./state/root.store";
import { baseURL, map as mapReducer } from "./state/map.slice";
import { eventStream } from "./event-stream";
import { defaultGroupName } from "./sidebar-library/sidebar-node.helpers";
import { DeleteNodesConfirmationComponent } from "./plan-library/delete-nodes-confirmation";
import { ShareMapDialog } from "./share-map-dialog";
import { assetOptions } from "./map-container.helpers";
import { exportMap } from "./services/exports";
import { SidebarButton, SidebarButtonWithTooltipTitleAndMessage } from "./sidebar-button";
import {
    canNodesBeCopied,
    canNodesBeGrouped,
    canNodesBeUnlinked,
    canNodesBeDeleted,
    createCompositionFeature,
    createObjectUpdateFromImport,
    getPermissionOnLayerSelection,
} from "./sidebar-toolbar-helpers";
import { getSelectedLayers } from "./map-root";
import { ModalState } from "./sidebar-types";
import { FROM_MAP_ROOT } from "./map-constants";
import { createObjects, deleteObjectsByLayerIds } from "./state/map.slice.thunks";
import { convertMapObjectsToNodes } from "./services/library-api-functions";
import { removeLayerObjectsFromStack, useMapUndoRedoContext } from "./map-undo-redo-context";
import { useMapLoading } from "./hooks/use-map-loading";
import { GroupActionValidationStatus, UnlinkLayerValidationStatus, DeleteActionValidationStatus, CopyActionValidationStatus } from "./sidebar-toolbar-types";
import { SATELLITE_MAP_BACKGROUND_TAG } from "./constants/map-background-constants";

export const SidebarToolbarComponent: FunctionComponent<{
    mapId: string;
    map: Node;
    getMapPosition: () => ExportOptions;
    handleDeleteNodes: (nodes: Node[]) => void;
    modal: ModalState;
    setModal: (modalState: ModalState) => void;
    handleCopyOrUnlinkLayers: (selectedLayers: MapLayer[], includeMapObjects: boolean, keepPermissions: boolean) => void;
    handleExportDwg: (selectedLayers: MapLayer[], exportOptions: MapObjectExportCadOptions) => void;
}> = ({ mapId, map, handleDeleteNodes, getMapPosition, handleCopyOrUnlinkLayers, modal, setModal, handleExportDwg }) => {
    const {
        mapSliceState,
        userFirstName,
        userLastName,
        selectedProject,
        userId,
        userScope,
        mappingEngine,
        takingScreenshot,
        isWebGLSupported,
        mapBackgrounds,
        selectedMapBackgroundId,
    } = useSelector((rootState: RootState) => ({
        mapSliceState: rootState.mapReducer,
        userFirstName: rootState.auth.user.firstName,
        userLastName: rootState.auth.user.lastName,
        userId: rootState.auth.user.userId,
        projectName: rootState.auth.projects,
        selectedProject: rootState.auth.projects.find((project) => rootState.auth.user.projectId === project.id),
        userScope: rootState.auth.user.userScope,
        mappingEngine: rootState.mapReducer.mappingEngine,
        takingScreenshot: rootState.mapReducer.takingScreenshot,
        isWebGLSupported: rootState.mapReducer.webGLSupported,
        mapBackgrounds: rootState.mapReducer.mapBackgrounds,
        selectedMapBackgroundId: rootState.mapReducer.mapModule.selectedMapBackgroundId,
    }));

    const [{ tree, isLoadingTree, selectedNodeIds }, treeBrowserContext] = useTreeBrowser(TreeBrowserDomainNodeContext, null, "tree", "isLoadingTree", "selectedNodeIds");
    const { dataFieldListItemsService } = useDataFieldServices();
    const undoRedo = useMapUndoRedoContext();

    const selectedLayers = getSelectedLayers(selectedNodeIds, mapSliceState.mapModule.layers.value);

    const editPermissionOnLayerSelection = useMemo(() => {
        const selectedLayerIds = selectedLayers.map(({ id }) => id);

        return (
            // Check permissions normally, if that fails
            getPermissionOnLayerSelection(map?.childNodes, selectedLayerIds) ||
            // If every selected node is a layer and every selected layer is imported then we should have edit permissions on the node (this is not the layer but the reference to the layer via a node)
            selectedLayerIds.every((id) => selectedLayers.every(({ id: layerId, imported }) => layerId === id && imported))
        );
    }, [map?.childNodes, selectedNodeIds, selectedLayers]);

    const groupActionValidationStatus = useMemo(() => {
        if (!map) {
            return GroupActionValidationStatus.MapLoading;
        }
        return canNodesBeGrouped(selectedNodeIds, map);
    }, [selectedNodeIds, map, editPermissionOnLayerSelection]);

    const unlinkActionValidationStatus = useMemo(() => {
        if (!map) {
            return UnlinkLayerValidationStatus.MapLoading;
        }
        return canNodesBeUnlinked(selectedLayers, selectedNodeIds, map);
    }, [selectedLayers, selectedNodeIds, map]);

    const copyActionValidationStatus = useMemo(() => {
        if (!map) {
            return CopyActionValidationStatus.MapLoading;
        }
        return canNodesBeCopied(selectedNodeIds, map);
    }, [selectedNodeIds, map]);

    const deleteActionValidationStatus = useMemo(() => {
        if (!map) {
            return DeleteActionValidationStatus.MapLoading;
        }
        return canNodesBeDeleted(selectedNodeIds, map);
    }, [selectedNodeIds, map]);

    const isRasterMapBackground = useMemo(() => {
        const foundMapBackground = mapBackgrounds.find((mapBackground) => mapBackground.id === selectedMapBackgroundId);
        return foundMapBackground?.tags?.some((tag) => tag.toLowerCase() === SATELLITE_MAP_BACKGROUND_TAG) ?? false;
    }, [mapBackgrounds, selectedMapBackgroundId]);

    const dispatch = useDispatch<RootDispatch>();
    const handleGroupLayers = (e: BaseSyntheticEvent) => {
        e.stopPropagation();
        const allSelectedNodes = selectedNodeIds.map((id) => findNode(tree.childNodes, id));
        const childNodes = getNodesWithoutParent(allSelectedNodes);
        if (groupActionValidationStatus === GroupActionValidationStatus.Valid) {
            // Create the group with the selected layers as children
            const group: Partial<Node> = {
                id: uuid(),
                status: Status.Active,
                name: defaultGroupName,
                parentId: childNodes[0].parentId,
                type: NodeType.Folder,
                childNodes,
                authorisation: Authorisation.Admin,
                childCount: childNodes.length,
            };
            group.sourceId = group.id;
            // Set the parent ids of the new child nodes of the group to the Id of the group so it can be added into the front end without waiting for network request.
            group.childNodes = group.childNodes.map((n) => {
                return { ...n, parentId: group.id };
            });
            // Remove the layers locally so we do not have duplicates
            treeBrowserContext.removeNodesLocally(selectedNodeIds);
            // Add the new group node to the state
            treeBrowserContext.addNode(group as Node);
            treeBrowserContext.expandNode(group as Node);
            // Deselect the layers
            treeBrowserContext.toggleSelectNodes([], { from: FROM_MAP_ROOT });
            dispatch(
                mapReducer.actions.updateLayerGroupIds(childNodes.filter((n) => n.type === NodeType.Layer).map((nodeLayer) => ({ layerId: nodeLayer.id, groupId: group.id })))
            );
        }
    };

    const openCopyLayersModal = (e: BaseSyntheticEvent) => {
        e.stopPropagation();
        setModal("copy");
    };

    const openUnlinkLayersModal = (e: BaseSyntheticEvent) => {
        e.stopPropagation();
        setModal("unlink");
    };

    const { layers } = useSelector((s: RootState) => ({ layers: s.mapReducer.mapModule.layers.value }));

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const [hasReadPermissionOnMap, hasEditPermissionsOnMap] = useReadWritePermissionCheck(map?.authorisation);

    // Because the selectedNodeIds is stored separate from the tree browser, the following findNodes() function may not find the recently selected node,
    // so we tell it to not throw an error if not found. A use case is when we create a layer and select it immediately.
    const nodesSelected = useMemo(() => findNodes([tree], selectedNodeIds, false), [tree, selectedNodeIds]);

    const groupToolTipMessage = useGroupLayerTooltip(groupActionValidationStatus);
    const unlinkTooltipMessage = useUnlinkLayerTooltip(unlinkActionValidationStatus);
    const copyTooltipMessage = useCopyLayerTooltip(copyActionValidationStatus);
    const deleteToolTipMessage = useDeleteActionTooltip(deleteActionValidationStatus);

    const openDeleteNodeModal = (e: BaseSyntheticEvent) => {
        e.stopPropagation();
        setModal("delete");
    };

    const translate = useIventisTranslate();

    const upgradePlan = useUpgradePlanAlert();

    const functionality = useFunctionality();

    const theme = useTheme<Theme>();

    const handleDeleteSelectedNodes = () => {
        nodesSelected.forEach((node) => {
            if (node.type === NodeType.Layer) {
                incrementMonitoringCount({ name: MetricName.DELETE_LAYER, resourceId: node.id });
            }
        });
        handleDeleteNodes(nodesSelected);
        setModal(null);
    };

    const isMapStateLoading = useMapLoading();
    const isMapLoadingOrConnecting = mappingEngine === MappingEngine.Mapbox ? isMapStateLoading : !mapSliceState.mapModule.digitalTwinEngineData.userConnectedToDigitalTwin;

    const { takeScreenshot } = useMapScreenshot({ allowScreenshot: () => !isLoadingTree && hasReadPermissionOnMap });
    const ctrlKey = useCtrlKey();

    return (
        <StyledBar>
            <div className="left">
                {mappingEngine === MappingEngine.Mapbox && (
                    <>
                        {/* Share map */}
                        <SidebarButton
                            toolTip={translate(Content.map3.sidebarToolbar.share)}
                            toolTipPlacement="top"
                            onClick={() => (functionality.sharedWorkspace || functionality.sharedLinks ? setModal("share") : functionality.open("sharedWorkspace"))}
                            disable={!hasEditPermissionsOnMap}
                            testId="share-map"
                        >
                            <FontAwesomeIcon
                                color={functionality.sharedWorkspace || functionality.sharedLinks ? "inherit" : theme.primaryColors.focus}
                                icon={["far", "share-nodes"]}
                            />
                        </SidebarButton>
                        {/* Export map */}
                        <SidebarButton
                            toolTip={translate(Content.map5.export_map.export_map)}
                            toolTipPlacement="top"
                            onClick={() => {
                                if (functionality.pdfExports) setModal("export");
                                else upgradePlan.open("pdfExports");
                            }}
                            disable={isMapLoadingOrConnecting || isLoadingTree || !hasReadPermissionOnMap || !isWebGLSupported || userScope === UserScope.External}
                            testId="export-map"
                        >
                            <FontAwesomeIcon color={functionality.pdfExports ? "inherit" : theme.primaryColors.focus} icon={["far", "file-pdf"]} />
                        </SidebarButton>
                    </>
                )}
                {/* Screenshot */}
                <SidebarButton
                    toolTip={<KeyboardShortcutToolTipComponent header={translate(Content.map10.screenshot.take_screenshot)} shortcut={`${ctrlKey} + ⇧ + S`} />}
                    toolTipPlacement="top"
                    onClick={() => {
                        takeScreenshot();
                    }}
                    disable={isMapLoadingOrConnecting || isLoadingTree || !hasReadPermissionOnMap || !isWebGLSupported}
                    testId="screenshot-map"
                >
                    {takingScreenshot ? <LoadingComponent size="13px" /> : <FontAwesomeIcon color="inherit" icon={["far", "camera"]} />}
                </SidebarButton>
            </div>
            <div>
                {mappingEngine === MappingEngine.Mapbox && (
                    <>
                        {/* Delete layers */}
                        <SidebarButtonWithTooltipTitleAndMessage
                            tooltipTitle={translate(Content.map11.map_delete_status.title)}
                            tooltipMessage={deleteToolTipMessage}
                            onClick={openDeleteNodeModal}
                            disable={deleteActionValidationStatus !== DeleteActionValidationStatus.Valid}
                            canUserEdit={editPermissionOnLayerSelection}
                            testId="delete-button"
                            disabledTestId="disabled-delete-button"
                        >
                            <FontAwesomeIcon icon={["far", "trash"]} />
                        </SidebarButtonWithTooltipTitleAndMessage>

                        {/* Duplicate layers */}
                        <SidebarButtonWithTooltipTitleAndMessage
                            tooltipTitle={translate(Content.map11.copy_status.title)}
                            tooltipMessage={copyTooltipMessage}
                            disable={copyActionValidationStatus !== CopyActionValidationStatus.Valid}
                            onClick={openCopyLayersModal}
                            testId="layer-copy-button"
                        >
                            <FontAwesomeIcon icon={["far", "copy"]} />
                        </SidebarButtonWithTooltipTitleAndMessage>

                        {/* Unlink layers */}
                        <SidebarButtonWithTooltipTitleAndMessage
                            tooltipTitle={translate(Content.map11.map_unlink_status.title)}
                            tooltipMessage={unlinkTooltipMessage}
                            disable={unlinkActionValidationStatus !== UnlinkLayerValidationStatus.Valid}
                            onClick={openUnlinkLayersModal}
                            testId="unlink-button"
                        >
                            <FontAwesomeIcon icon={["far", "link-slash"]} />
                        </SidebarButtonWithTooltipTitleAndMessage>

                        {/* Group layers */}
                        <SidebarButtonWithTooltipTitleAndMessage
                            tooltipTitle={translate(Content.map11.map_group_status.title)}
                            tooltipMessage={groupToolTipMessage}
                            onClick={handleGroupLayers}
                            disable={groupActionValidationStatus !== GroupActionValidationStatus.Valid}
                            canUserEdit={editPermissionOnLayerSelection}
                            testId="group-button"
                            disabledTestId="disabled-group-button"
                        >
                            <FontAwesomeIcon icon={["far", "object-ungroup"]} />
                        </SidebarButtonWithTooltipTitleAndMessage>
                    </>
                )}
            </div>
            <DeleteNodesConfirmationComponent
                modalLocation="map"
                nodes={nodesSelected}
                show={modal === "delete"}
                onConfirm={handleDeleteSelectedNodes}
                onClose={() => setModal(null)}
            />
            {hasEditPermissionsOnMap && modal === "share" && (
                <ShareMapDialog isPersonalLibrary={tree?.type === NodeType.PersonalMappingLibrary} isOpen onClose={() => setModal(null)} />
            )}
            {hasReadPermissionOnMap && modal === "export" && (
                <ExportMapDialog
                    initialState={mapSliceState.mapModule}
                    map={map}
                    isOpen
                    onClose={() => setModal(null)}
                    signatures={mapSliceState.signatures}
                    mapApiFunctions={{
                        ...assetOptions,
                        exportMap,
                        mapTilesGetter: () => [{ name: "iventis", tiles: [`${baseURL}map/${mapId}/tiles/{z}/{x}/{y}.pbf?ignoreZoomOptimisation=true`], type: "vector" }],
                        getAttributeListItems: dataFieldListItemsService.getDataFieldListItems,
                    }}
                    getMapStartingPosition={getMapPosition}
                    mapLayers={layers.filter((layer) => {
                        if (!layer.remote || !layer.visible) return false;

                        const node = findNode(map.childNodes, layer.id);
                        if (!node) {
                            // eslint-disable-next-line no-console
                            console.error(`Layer ${layer.id} not found in map ${map.id}. This is likely a bug.`);
                            return false;
                        }
                        return canRead(node.authorisation);
                    })}
                    layersWithTemporaryVisibilityValue={mapSliceState.layersWithTemporaryVisibilityValue}
                    userFirstName={userFirstName}
                    userLastName={userLastName}
                    projectName={selectedProject.name}
                    projectLogo={selectedProject.logoUrl}
                    levelIndex={mapSliceState.mapModule.currentLevel ?? 0}
                    // 3D sitemaps are not supported for exports
                    selectedSitemapVersions={getSelectedSitemapVersions(mapSliceState.sitemaps, mapSliceState.sitemapConfigs)?.filter(
                        (sv) => sv.type === SitemapVersionType.TwoDimensional
                    )}
                    projectId={selectedProject.id}
                    userId={userId}
                    dateFilter={
                        mapSliceState?.mapModule?.datesFilter?.value?.filter && mapSliceState?.mapModule?.datesFilter?.value?.day != null
                            ? parseEpochSecondsToDateOnlyString(mapSliceState?.mapModule?.datesFilter?.value?.day)
                            : null
                    }
                    timeFilter={
                        mapSliceState?.mapModule?.datesFilter?.value?.filter && mapSliceState?.mapModule?.datesFilter?.value?.time != null
                            ? parseTimeHHMMNumbertoHHMMSSString(mapSliceState?.mapModule?.datesFilter?.value?.time)
                            : null
                    }
                    zoomChanged={() => undefined}
                    pitchChanged={() => undefined}
                    paperSizes={isRasterMapBackground ? PAGE_SIZES_FOR_RASTER : undefined}
                />
            )}

            {hasEditPermissionsOnMap && (modal === "copy" || modal === "unlink") && (
                <UnlinkOrCopyLayerDialog
                    hasNoMapObjects={false}
                    translate={translate}
                    onClose={() => setModal(null)}
                    open
                    stage={modal === "unlink" ? 1 : 0}
                    onConfirm={(includeMapObjects, keepPermissions) => handleCopyOrUnlinkLayers(selectedLayers, includeMapObjects, keepPermissions)}
                    getImportedLayerMapNames={getImportedLayerMapNames}
                    nodesSelected={nodesSelected}
                    selectedLayers={selectedLayers}
                />
            )}
            {hasEditPermissionsOnMap && modal === "exportDwg" && (
                <ExportDwgDialog translate={translate} onClose={() => setModal(null)} open onConfirm={(options) => handleExportDwg(selectedLayers, options)} />
            )}
            {hasEditPermissionsOnMap && modal === "importGeoJson" && (
                <ImportGeoJsonContext.Provider
                    value={{
                        projectAttributes: mapSliceState.mapModule.projectDataFields,
                        getListItem: (attributeId: string) => dataFieldListItemsService.getDataFieldListItems(attributeId),
                        postMapObjects: async (mapObjects: PostImportedGeoJsonMapObject[], overwriteExisting: boolean) => {
                            eventStream.next({ type: machineEventTypes.cancelDrawing });
                            const layer = selectedLayers[0];
                            const layerNode = findNode([tree], layer.id);

                            if (overwriteExisting) {
                                await dispatch(deleteObjectsByLayerIds({ layerIds: [layer.id] }));
                                treeBrowserContext.removeNodesLocally(layerNode.childNodes.map((node) => node.id));
                                // If we are overriding the existing objects, we need to remove them from the undo/redo stack
                                undoRedo.removeFromStack((acc, entry) => removeLayerObjectsFromStack(acc, entry, [layer.id]));
                                // Refresh tile sources to remove all remote objects
                                eventStream.next({
                                    type: "REFRESH_SOURCE_TILES",
                                    payload: { sourceNames: Object.values(mapSliceState.mapModule.tileSources.value).flatMap((source) => source.tiles.map(({ name }) => name)) },
                                });
                            }

                            // Make API request to create the imported objects
                            const mapObjectUpdates: ObjectUpdate[] = mapObjects.map((feature) =>
                                createObjectUpdateFromImport(feature, mapSliceState.mapModule.currentLevel, mapSliceState.sitemaps, mapSliceState.sitemapConfigs)
                            );

                            // Spilt the new objects into smaller amount of objects
                            const updates = splitArrayByLength(mapObjectUpdates, 200);
                            const requests = updates.map((update) => dispatch(createObjects(update)));
                            await Promise.all(requests);

                            // Add the map objects to the sidebar
                            const mapObjectNodes = convertMapObjectsToNodes(layerNode, mapObjects, getMapObjectNameSystemDatafield(mapSliceState.mapModule.projectDataFields).id);
                            treeBrowserContext.addNodesLocally(mapObjectNodes);

                            // Add the map objects locally to the map
                            const localFeatures: CompositionMapObject[] = mapObjects.map((feature) => createCompositionFeature(feature, mapSliceState.mapModule.currentLevel));
                            dispatch(mapReducer.actions.setLocalGeoJson(localFeatures));

                            // Close the modal
                            setModal(null);
                        },
                    }}
                >
                    <StyledCustomDialog $overflow="hidden" maxWidth={false} open onClose={() => setModal(null)}>
                        <StyledDialogTitle>
                            <FontAwesomeIcon icon={["fas", "download"]} /> {translate(Content.map6.fileUpload.import_title)}
                        </StyledDialogTitle>
                        <ImportGeojsonFileUploadComponent mapLayer={selectedLayers[0]} />
                    </StyledCustomDialog>
                </ImportGeoJsonContext.Provider>
            )}
        </StyledBar>
    );
};

const getImportedLayerMapNames = async (nodes: Node[]): Promise<NodeWithMapNode[]> => {
    // Get the nodes which have been imported from another plan
    const importedNodes = nodes.filter((node) => node.id !== node.sourceId);

    // Fetch the origin nodes of the imported nodes
    const originBottomUpRequests = importedNodes.map((node) => libraryApi.get<BottomUpHierarchy<Node>>(`nodes/${node.sourceId}/parents`));
    const getOriginNodesResponse = await Promise.all(originBottomUpRequests);

    // Fetch the origin plans
    const mapNodeRequests = getOriginNodesResponse.map(({ data: originTree }) =>
        libraryApi.get<Node>(`/nodes/${findFirstParentByType(originTree, NodeType.Map).id}`, { params: { expandNodeType: [NodeType.Map] } })
    );
    const getMapNodeResponse = await Promise.all(mapNodeRequests).then((response) => response.map((d) => d.data));

    return importedNodes.map((importedNode, index) => ({
        ...importedNode,
        mapId: getMapNodeResponse[index].id,
        mapName: getMapNodeResponse[index].name,
    }));
};

const StyledDialogTitle = styled(DialogTitle)`
    display: flex;
    gap: 8px;
    place-items: center;
`;
